import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { RegistrationState } from "../../api/pbxExtensionsApi";
import { STATUS_OPTIONS } from "../../lib/destinationTransfer";
import { fmtDuration } from "../../components/reports/ReportBits";

/** Shared pieces of the IPPBX → Extensions pages. */

export const STATE_LABELS: Record<RegistrationState, string> = {
  registered: "Registered",
  unregistered: "Unregistered",
  expired: "Expired",
  never: "Never registered",
};

const STATE_STYLES: Record<RegistrationState, { bg: string; fg: string; bd: string; dot: string }> = {
  registered: { bg: "var(--portal-status-success-soft)", fg: "var(--portal-status-success-darker)", bd: "var(--portal-status-success)", dot: "var(--portal-status-success)" },
  expired: { bg: "#fff4e0", fg: "#8a5300", bd: "#e0a83f", dot: "#e0a83f" },
  unregistered: { bg: "#fdecea", fg: "#a12a1f", bd: "#e74c3c", dot: "#c0392b" },
  never: { bg: "var(--portal-surface)", fg: "var(--portal-muted)", bd: "var(--portal-border)", dot: "var(--portal-border)" },
};

/** "42 s ago", "5 m ago", "3 h ago", "2 d ago". */
export function fmtAgo(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)} s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ${Math.floor((seconds % 3600) / 60)} m ago`;
  return `${Math.floor(seconds / 86400)} d ago`;
}

/** Seconds between an ISO instant and now (UTC), for "ago" labels of arbitrary timestamps. */
export function secondsSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso.endsWith("Z") || /[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, (Date.now() - d.getTime()) / 1000);
}

export function RegistrationChip({ state, detail, size = "md" }: { state: RegistrationState; detail?: string; size?: "sm" | "md" }) {
  const s = STATE_STYLES[state];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: size === "sm" ? "1px 8px" : "3px 10px",
        borderRadius: 999,
        border: `1px solid ${s.bd}`,
        background: s.bg,
        color: s.fg,
        fontSize: size === "sm" ? "0.72rem" : "0.8rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, flex: "none" }} />
      {STATE_LABELS[state]}
      {detail && <span style={{ fontWeight: 400, opacity: 0.85 }}>· {detail}</span>}
    </span>
  );
}

/** Same shape as RegistrationChip, blue, for an extension that is on a call right now. */
export function OnCallChip({ count, size = "md" }: { count: number; size?: "sm" | "md" }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: size === "sm" ? "1px 8px" : "3px 10px",
        borderRadius: 999,
        border: "1px solid #93c5fd",
        background: "#e8f1ff",
        color: "#1d4ed8",
        fontSize: size === "sm" ? "0.72rem" : "0.8rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: "#2b7fff", flex: "none" }} />
      On call{count > 1 ? ` ×${count}` : ""}
    </span>
  );
}

/** The 4-digit extension number colleagues dial (dbo.Agent.pbxExtension); a dash when none is assigned. */
export function ExtensionBadge({ extension, size = "md", title }: { extension: string | null | undefined; size?: "sm" | "md"; title?: string }) {
  const has = !!extension && extension.trim().length > 0;
  return (
    <span
      title={title ?? (has ? `Extension ${extension}` : "No extension number assigned")}
      style={{
        display: "inline-block", padding: size === "sm" ? "0 6px" : "1px 8px", borderRadius: 6, fontSize: size === "sm" ? "0.72rem" : "0.78rem", fontWeight: 700,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em", whiteSpace: "nowrap",
        background: has ? "#eef2ff" : "var(--portal-surface)", color: has ? "#3730a3" : "var(--portal-muted)", border: `1px solid ${has ? "#c7d2fe" : "var(--portal-border)"}`,
      }}
    >
      {has ? extension : "—"}
    </span>
  );
}

export function Pill({ children, tone = "info" }: { children: ReactNode; tone?: "info" | "warn" | "muted" }) {
  const styles = tone === "warn"
    ? { background: "#fff4e0", color: "#8a5300", border: "1px solid #e0a83f" }
    : tone === "muted"
      ? { background: "var(--portal-surface)", color: "var(--portal-muted)", border: "1px solid var(--portal-border)" }
      : { background: "#e8f1ff", color: "#1d4ed8", border: "1px solid #93c5fd" };
  return (
    <span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap", ...styles }}>
      {children}
    </span>
  );
}

/**
 * Phone model from a SIP User-Agent: "Yealink SIP-T46S 66.85.0.5" → "Yealink SIP-T46S",
 * "Grandstream GXP2170 1.0.11.79" → "Grandstream GXP2170", "Z 5.6.3 rv2.10.20.6" → "Zoiper",
 * "MicroSIP/3.21.3" → "MicroSIP", "Linphone/5.2 (…)" → "Linphone". Pure, so it can be unit-tested.
 */
export function phoneModel(userAgent: string | null | undefined): string {
  const ua = (userAgent ?? "").trim();
  if (!ua) return "—";
  if (/^Z\s+\d/.test(ua) || /zoiper/i.test(ua)) return "Zoiper";
  if (/^snom/i.test(ua)) return ua.split(/[\s/]/).slice(0, 2).join(" ");
  const slash = ua.match(/^([A-Za-z][\w.\- ]*?)\/[\d.]/);
  if (slash) return slash[1].trim();
  const words = ua.split(/\s+/).filter((w) => !/^v?\d+(\.\d+)+$/.test(w) && !/^\(.*\)$/.test(w) && !/^rv[\d.]+$/i.test(w));
  return words.slice(0, 3).join(" ") || ua;
}

/** Outbound identity for the lists: the number, "Hidden", "From", or "–" when outbound calling is denied. */
export function identityLabel(mode: string, number: string | null): string {
  if (mode === "number") return number ? `+${number}` : "Number (invalid)";
  if (mode === "hidden") return number ? `Hidden (+${number})` : "Hidden";
  if (mode === "from") return number ? `From (fallback +${number})` : "From (fallback invalid)";
  return "–";
}

export function statusLabel(statusId: number, fallback: string): string {
  return STATUS_OPTIONS.find((s) => s.id === statusId)?.label ?? fallback;
}

type PillTone = "success" | "info" | "warn" | "danger" | "muted";

const PILL_TONES: Record<PillTone, { bg: string; fg: string; bd: string }> = {
  success: { bg: "var(--portal-status-success-soft)", fg: "var(--portal-status-success-darker)", bd: "var(--portal-status-success)" },
  info: { bg: "#e8f1ff", fg: "#1d4ed8", bd: "#93c5fd" },
  warn: { bg: "#fff4e0", fg: "#8a5300", bd: "#e0a83f" },
  danger: { bg: "#fdecea", fg: "#a12a1f", bd: "#e74c3c" },
  muted: { bg: "var(--portal-surface)", fg: "var(--portal-muted)", bd: "var(--portal-border)" },
};

/** Tone + label of a StatsAgentLog type (ids from the legacy LogType table, names for the PBX_OUT_* rows). */
export function logTypePresentation(logTypeId: number, logTypeName: string): { tone: PillTone; label: string } {
  const name = (logTypeName ?? "").toUpperCase();
  switch (name) {
    case "PBX_OUT_ATTEMPT": return { tone: "info", label: "Outbound attempt" };
    case "PBX_OUT_ANSWERED": return { tone: "success", label: "Outbound answered" };
    case "PBX_OUT_ENDED": return { tone: "muted", label: "Outbound ended" };
    case "PBX_OUT_FAILED": return { tone: "warn", label: "Outbound failed" };
    case "PBX_OUT_DENIED": return { tone: "danger", label: "Outbound denied" };
    case "PBX_IN_DND": return { tone: "danger", label: "Phone DND" };
    case "PBX_INT_ATTEMPT": return { tone: "info", label: "Internal call" };
    case "PBX_INT_ANSWERED": return { tone: "success", label: "Internal answered" };
    case "PBX_INT_ENDED": return { tone: "muted", label: "Internal ended" };
    case "PBX_INT_FAILED": return { tone: "warn", label: "Internal failed" };
    case "PBX_INT_DENIED": return { tone: "danger", label: "Internal denied" };
    case "PBX_INT_VOICEMAIL": return { tone: "info", label: "Voicemail" };
    case "PBX_TRANSFER_OUT": return { tone: "info", label: "Transferred out" };
    case "PBX_TRANSFER_IN": return { tone: "success", label: "Transferred in" };
    case "PBX_TRANSFER_FAILED": return { tone: "warn", label: "Transfer failed" };
  }
  switch (logTypeId) {
    case 1: return { tone: "muted", label: "Unavailable" };
    case 2: return { tone: "warn", label: "Busy (system)" };
    case 3: return { tone: "warn", label: "Busy" };
    case 4: return { tone: "info", label: "Ringing" };
    case 5:
    case 6: return { tone: "success", label: "Answered" };
    case 8: return { tone: "warn", label: "No answer" };
    case 9: return { tone: "danger", label: "Rejected / error" };
    case 10: return { tone: "muted", label: "Call ended" };
    case 17: return { tone: "info", label: "Status change" };
    case 18: return { tone: "info", label: "Request" };
    case 22: return { tone: "muted", label: "Caller hung up" };
    case 23: return { tone: "warn", label: "Auto sign-off" };
    case 35: return { tone: "success", label: "PBX register" };
    case 36: return { tone: "danger", label: "PBX unregister" };
    default: return { tone: "muted", label: logTypeName ? logTypeName.charAt(0) + logTypeName.slice(1).toLowerCase().replace(/_/g, " ") : `#${logTypeId}` };
  }
}

export function LogTypePill({ logTypeId, logTypeName, title }: { logTypeId: number; logTypeName: string; title?: string }) {
  const p = logTypePresentation(logTypeId, logTypeName);
  const t = PILL_TONES[p.tone];
  return (
    <span title={title} style={{ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap", background: t.bg, color: t.fg, border: `1px solid ${t.bd}` }}>
      {p.label}
    </span>
  );
}

/**
 * "-> 0263 (Name) blind" / "<- 0261 (Name) attended" (the info string of PBX_TRANSFER_OUT / _IN, script 20)
 * → "to extension 0263 (Name), blind transfer". Pure, so it can be unit-tested.
 */
export function transferDetail(infoString: string | null | undefined): string {
  const s = (infoString ?? "").trim();
  const m = /^(->|<-)\s*(\S+)\s*(\(.*\))?\s*(blind|attended)?$/i.exec(s);
  if (!m) return s;
  const dir = m[1] === "->" ? "to" : "from";
  const kind = m[4] ? `, ${m[4].toLowerCase()} transfer` : "";
  return `${dir} extension ${m[2]}${m[3] ? ` ${m[3]}` : ""}${kind}`;
}

/** Readable detail for a StatsAgentLog row. */
export function logDetail(logTypeId: number, logTypeName: string, infoString: string | null, infoInt: number | null): string {
  // Outbound calls from the extension (LogType rows added by script 16, matched by name).
  switch ((logTypeName ?? "").toUpperCase()) {
    case "PBX_OUT_ATTEMPT": return `Dialled ${infoString ?? "?"}`;
    case "PBX_OUT_ANSWERED": return `Answered by ${infoString ?? "?"}`;
    case "PBX_OUT_ENDED": return infoInt != null ? `Call to ${infoString ?? "?"} ended after ${fmtDuration(infoInt)}` : `Call to ${infoString ?? "?"} ended`;
    case "PBX_OUT_FAILED": return `Not connected: ${infoString ?? "?"}`;
    case "PBX_OUT_DENIED": return `Denied: ${infoString ?? "?"}`;
    case "PBX_IN_DND": return infoString ? `Phone rejected the call from ${infoString} (DND or blocked line)` : "Phone rejected the call (DND or blocked line)";
    case "PBX_INT_ATTEMPT": return `Dialled extension ${infoString ?? "?"}`;
    case "PBX_INT_ANSWERED": return `Extension ${infoString ?? "?"} answered`;
    case "PBX_INT_ENDED": return infoInt != null ? `Call with ${infoString ?? "?"} ended after ${fmtDuration(infoInt)}` : `Call with ${infoString ?? "?"} ended`;
    case "PBX_INT_FAILED": return `Not connected: ${infoString ?? "?"}`;
    case "PBX_INT_DENIED": return `Denied: ${infoString ?? "?"}`;
    case "PBX_INT_VOICEMAIL": return infoString ?? "Caller left a voicemail";
    case "PBX_TRANSFER_OUT": return `Transferred the caller ${transferDetail(infoString)}`;
    case "PBX_TRANSFER_IN": return `Received a transferred caller ${transferDetail(infoString)}`;
    case "PBX_TRANSFER_FAILED": return `Transfer failed: ${infoString ?? "?"}`;
  }
  switch (logTypeId) {
    case 10:
      return infoInt != null ? `Call ended after ${fmtDuration(infoInt)}` : "Call ended";
    case 17:
      return infoInt != null ? `Status → ${statusLabel(infoInt, `#${infoInt}`)}` : "Status changed";
    case 35:
      return infoInt != null ? `Registered on PBX #${infoInt}` : "Registered on PBX";
    case 36:
      return infoInt != null ? `Unregistered from PBX #${infoInt}` : "Unregistered from PBX";
    default: {
      const parts = [infoString, infoInt != null ? String(infoInt) : null].filter((x): x is string => !!x && x.trim().length > 0);
      return parts.join(" · ");
    }
  }
}

export function Pager({ page, pageSize, total, onPage, busy }: { page: number; pageSize: number; total: number; onPage: (p: number) => void; busy?: boolean }) {
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", fontSize: "0.85rem", color: "var(--portal-muted)" }}>
      <span>
        {total === 0 ? "No rows" : `${from}–${to} of ${total}`}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <button type="button" className="btn btn-secondary" onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1 || busy} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}>
          <ChevronLeft size={14} /> Prev
        </button>
        <span>Page {page} of {pageCount}</span>
        <button type="button" className="btn btn-secondary" onClick={() => onPage(Math.min(pageCount, page + 1))} disabled={page >= pageCount || busy} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}>
          Next <ChevronRight size={14} />
        </button>
      </span>
    </div>
  );
}

export function SegmentedFilter<T extends string>({ value, options, onChange, ariaLabel }: { value: T; options: { value: T; label: string; count?: number }[]; onChange: (v: T) => void; ariaLabel: string }) {
  return (
    <div role="group" aria-label={ariaLabel} style={{ display: "inline-flex", background: "var(--portal-surface-alt)", borderRadius: 999, padding: 3, gap: 2, flexWrap: "wrap" }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            style={{
              border: "none",
              background: active ? "white" : "transparent",
              color: active ? "var(--portal-text)" : "var(--portal-muted)",
              boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
              fontFamily: "inherit",
              fontSize: "0.82rem",
              fontWeight: 600,
              padding: "5px 12px",
              borderRadius: 999,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {o.label}
            {o.count != null && <span style={{ marginLeft: 6, opacity: 0.7, fontWeight: 500 }}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export const inputStyle: React.CSSProperties = { padding: "6px 8px", border: "1px solid var(--portal-border)", borderRadius: 6, fontSize: "0.85rem", fontFamily: "inherit", background: "white" };
export const monoStyle: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.8rem" };

/** "yyyy-MM-ddTHH:mm" wall-clock in a zone, for datetime-local inputs. */
export function wallClockInTz(d: Date, tz: string): string {
  let f: Intl.DateTimeFormat;
  try {
    f = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    f = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  }
  const get = (type: string) => f.formatToParts(d).find((x) => x.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour") === "24" ? "00" : get("hour")}:${get("minute")}`;
}
